/**
 * Pure presenter helpers for report blocks.
 *
 * These were extracted out of the `ReportBlock` component so the rendering
 * logic (JSX) is separable from the pure "what string / shape / URL should
 * this block produce" decisions. Everything here is side-effect-free and
 * unit-testable in isolation; `ReportBlock` re-exports the ones the test
 * suite consumes so existing import paths keep resolving.
 */
import type { BlockSpec } from "./schema";
import { humanizeAny } from "./labels";

/**
 * Translate the most common cube / adapter error strings into something
 * a recruiter can act on. Raw error text falls through unchanged so we
 * don't hide unexpected failures during debugging.
 */
export function humanizeBlockError(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  const lower = raw.toLowerCase();
  if (lower.includes("network") || lower.includes("failed to fetch") || lower.includes("err_network")) {
    return "Lost connection to the data layer. Refresh the page to retry.";
  }
  if (lower.includes("401") || lower.includes("unauthor")) {
    return "Your session expired. Sign in again to reload this block.";
  }
  if (lower.includes("403") || lower.includes("forbidden")) {
    return "You don't have access to one of the measures or dimensions in this block.";
  }
  if (lower.includes("404")) {
    return "A measure or dimension this block uses isn't available right now.";
  }
  if (lower.includes("cube") && (lower.includes("not found") || lower.includes("unknown"))) {
    return "A measure in this block isn't wired to live data yet — try a different metric.";
  }
  if (lower.includes("invalid query format") && lower.includes("measures")) {
    return "A metric in this block isn't connected to report data yet — open the editor and choose another metric.";
  }
  // "No CandidateSkill.uniqueCandidates found" — Cube returns this when
  // the filter combo matches zero rows. From the user's POV the metric
  // exists; it just has no matching data. Nudge toward filter/range.
  if (/no\s+[a-z.]+\s+found/i.test(raw)) {
    return "No rows matched. Try widening the time range or removing a filter.";
  }
  // "Invalid query format: dimensions[0] with value 'X'" — adapter
  // couldn't translate a short dim alias. Nudge toward editing the block.
  if (lower.includes("invalid query format") && lower.includes("dimensions")) {
    return "One of the dimensions in this block isn't recognised — open the editor and reselect it.";
  }
  // "Can't parse date: 'ytd'" — agent emitted a range token the adapter
  // doesn't expand. The repair pass now catches this but if it slips:
  if (lower.includes("can't parse date") || lower.includes("cannot parse date")) {
    return "The date range on this block isn't recognised — pick one from the time-range picker.";
  }
  if (lower.includes("timeout") || lower.includes("timed out")) {
    return "Query took too long. Narrow the range or use coarser granularity.";
  }
  if (lower.includes("continue wait")) {
    return "Cube is still warming up. Refresh in a moment.";
  }
  // OpenAI / AI provider quota — the wizard route surfaces the raw
  // message because there's nothing the user can do to refine the
  // prompt around it. Rewrite to point at the env config instead.
  if (lower.includes("exceeded your current quota") || lower.includes("insufficient_quota")) {
    return "The AI provider is over its usage quota. Update the OPENAI_API_KEY in the server's .env file.";
  }
  if (lower.includes("invalid api key") || lower.includes("invalid_api_key")) {
    return "The AI provider rejected the API key. Update OPENAI_API_KEY in the server's .env file.";
  }
  return raw;
}

/**
 * Pick the empty-state copy for a block that returned zero rows.
 * Pure; exported for tests.
 *
 *   - "no <measure> found for <range>" headline
 *   - tip varies by whether the spec has filters (suggest removal) and
 *     whether the range is tight (suggest a wider one).
 */
export interface EmptyStateText {
  title: string;
  tip: string;
}

export function buildEmptyStateText(block: BlockSpec): EmptyStateText | null {
  if (!("query" in block) || !block.query) return null;
  const measure = block.query.measures?.[0] ?? "this metric";
  const range = block.query.timeDimension?.range;
  const filters = block.query.filters ?? [];
  const tightRanges = new Set<string>([
    "today",
    "yesterday",
    "this_week",
    "last_week",
    "last_7_days",
  ]);
  const tightRange = typeof range === "string" && tightRanges.has(range);
  const tip = filters.length > 0
    ? "Try removing a filter or widening the time range."
    : tightRange
      ? "Try widening the time range — even Last 30 days or This quarter often surface enough data."
      : "There's nothing matching this query right now.";
  const title =
    `No ${String(measure).replace(/_/g, " ")} found` +
    (range ? ` for ${String(range).replace(/_/g, " ")}` : "");
  return { title, tip };
}

/**
 * Title fallback ladder:
 *   1. Author-set block.title (always wins).
 *   2. Humanised first measure for kpi/chart/table/insight (e.g.
 *      "float_open_rate" → "Float open rate"). Multiple measures get
 *      "·"-joined.
 *   3. "Notes" / "Embed" for text/embed.
 *   4. labelFor(block.type) as a last resort.
 */
export function deriveBlockTitle(block: BlockSpec): string {
  if ("title" in block && typeof block.title === "string" && block.title.length > 0) {
    if (block.title === "New table" || block.title === "New chart" || block.title === "New KPI") {
      // Fall through to the metric-based title below. Placeholder titles are
      // useful while constructing specs, but they read as unfinished UI.
    } else {
      return block.title;
    }
  }
  if (
    (block.type === "kpi" ||
      block.type === "chart" ||
      block.type === "table" ||
      block.type === "insight") &&
    "query" in block &&
    Array.isArray(block.query?.measures) &&
    block.query.measures.length > 0
  ) {
    const labels = block.query.measures.slice(0, 3).map(humanizeAny);
    return block.query.measures.length > 3
      ? `${labels.join(" · ")} · …`
      : labels.join(" · ");
  }
  return labelFor(block);
}

export function skeletonShapeFor(block: BlockSpec): "kpi" | "bar" | "line" | "table" | "default" {
  if (block.type === "kpi") return "kpi";
  if (block.type === "table") return "table";
  if (block.type === "chart") {
    const t = block.viz?.type;
    if (t === "line" || t === "area") return "line";
    if (
      t === "bar" ||
      t === "stacked_bar" ||
      t === "horizontal_bar" ||
      t === "waterfall"
    ) {
      return "bar";
    }
  }
  return "default";
}

export function labelFor(block: BlockSpec): string {
  return (
    {
      kpi: "KPI",
      chart: "Chart",
      table: "Table",
      insight: "Insight",
      text: "Notes",
      embed: "Embed",
    }[block.type] ?? "Block"
  );
}

/**
 * Build the per-block CSV-export URL hit by the "Download CSV" action.
 * Pure (just string assembly) so the action wiring in the component stays
 * thin and the URL shape is testable.
 */
export function buildCsvExportUrl(reportId: string, blockId: string): string {
  return `/api/reports/${encodeURIComponent(reportId)}/export/csv?block=${encodeURIComponent(blockId)}`;
}

/**
 * Build the authenticated single-block view used by the per-section share
 * menu. `origin` is optional so tests and server code can assert the route
 * shape without depending on a browser global; callers that copy to the
 * clipboard should pass `window.location.origin` to produce a full URL.
 */
export function buildBlockShareUrl(reportId: string, blockId: string, origin?: string): string {
  const path = `/reports/${encodeURIComponent(reportId)}/blocks/${encodeURIComponent(blockId)}`;
  return origin ? `${origin.replace(/\/$/, "")}${path}` : path;
}

/**
 * Single source of truth for embed entity types. The keys MUST match the
 * `EmbedBlockSpec.entity.type` enum in schema/index.ts — both the embed
 * editor's option list and the renderer's icon/path maps derive from this so
 * they can't drift (they did: the renderer used to map `company`, which isn't
 * a valid entity type).
 */
export const ENTITY_TYPES = [
  { value: "candidate", label: "Candidate", icon: "👤", path: "/candidates" },
  { value: "contact", label: "Contact", icon: "🤝", path: "/contacts" },
  { value: "job", label: "Job", icon: "📄", path: "/jobs" },
  { value: "float", label: "Float", icon: "🎈", path: "/floats" },
  { value: "submission", label: "Submission", icon: "📤", path: "/submissions" },
] as const satisfies ReadonlyArray<{ value: EmbedEntityType; label: string; icon: string; path: string }>;

type EmbedEntityType = NonNullable<Extract<BlockSpec, { type: "embed" }>["entity"]>["type"];

export const ENTITY_ICONS: Record<string, string> = Object.fromEntries(
  ENTITY_TYPES.map((e) => [e.value, e.icon]),
);

export const ENTITY_PATHS: Record<string, string> = Object.fromEntries(
  ENTITY_TYPES.map((e) => [e.value, e.path]),
);
